import { ref } from "vue";

export type AppFilterValues = Record<string, string[]>;

export function cloneFilterValues(values: AppFilterValues): AppFilterValues {
  const out: AppFilterValues = {};
  for (const key of Object.keys(values)) out[key] = [...values[key]!];
  return out;
}

// `draft` = sélection en cours dans le panneau ; `applied` = filtres validés (mémoire seulement).
export function useAppMemoryFilters(initial?: AppFilterValues) {
  const applied = ref<AppFilterValues>(initial ? cloneFilterValues(initial) : {});
  const draft = ref<AppFilterValues>(cloneFilterValues(applied.value));

  function syncDraftFromApplied() {
    draft.value = cloneFilterValues(applied.value);
  }

  function apply() {
    applied.value = cloneFilterValues(draft.value);
  }

  function clearDraft() {
    draft.value = {};
  }

  function clearAppliedKey(key: string, value?: string) {
    const next = cloneFilterValues(applied.value);
    if (value === undefined) {
      delete next[key];
    } else if (next[key]) {
      next[key] = next[key].filter((v) => v !== value);
      if (!next[key].length) delete next[key];
    }
    applied.value = next;
  }

  function toggleDraft(key: string, value: string) {
    const arr = draft.value[key] ?? [];
    const next = { ...draft.value };
    if (arr.includes(value)) {
      const filtered = arr.filter((v) => v !== value);
      if (filtered.length) next[key] = filtered;
      else delete next[key];
    } else {
      next[key] = [...arr, value];
    }
    draft.value = next;
  }

  function setDraftValues(key: string, values: string[]) {
    const next = { ...draft.value };
    const unique = Array.from(new Set(values));
    if (unique.length) next[key] = unique;
    else delete next[key];
    draft.value = next;
  }

  function isDraftSelected(key: string, value: string) {
    return draft.value[key]?.includes(value) ?? false;
  }

  return {
    applied,
    draft,
    apply,
    clearDraft,
    clearAppliedKey,
    syncDraftFromApplied,
    toggleDraft,
    setDraftValues,
    isDraftSelected,
  };
}
